/**
 * Types for the Business Support "Candidates Data" surface.
 *
 * Mirrors the backend DTOs in `com.reverse.job.portal.CandidateData.DTO`. The same directory is
 * served two ways - authenticated for the admin console and through an opaque token for external
 * recipients - so the search request and row shapes are shared between both pages.
 */

export type ShareAudience = 'CANDIDATE' | 'RECRUITER';

export type DataShareStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';

/** Persisted verbatim on the backend; the tracking panel groups on these names. */
export type ShareEventType =
  | 'LINK_CREATED'
  | 'LINK_SENT'
  | 'LINK_OPENED'
  | 'FILTERS_APPLIED'
  | 'RESULT_VIEWED'
  | 'EXPORTED'
  | 'ONBOARD_CLICKED'
  | 'ONBOARD_SENT';

/** A directory row for a candidate. */
export interface CandidateDataRow {
  id: string;
  fullName: string;
  email: string | null;
  city: string | null;
  currentRole: string | null;
  currentCompany: string | null;
  totalYearsOfExperience: number | null;
  roleLevel: string | null;
  targetRoles: string[] | null;
  coreExpertise: string[] | null;
  availabilityStatus: string | null;
  workFlexibility: string | null;
  decisionSpeed: string | null;
  expectedCompensation: number | null;
  overallScore: number | null;
  profileCompleteScore: number | null;
  userStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** A directory row for a recruiter (the reverse view). */
export interface RecruiterDataRow {
  id: string;
  fullName: string;
  email: string | null;
  designation: string | null;
  companyId: string | null;
  companyAdmin: boolean | null;
  active: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type DataRow = CandidateDataRow | RecruiterDataRow;

/** One facet bucket. Clicking it applies that value as a filter. */
export interface FacetCount {
  value: string;
  count: number;
}

/** A named group of facet buckets (e.g. "City", "Availability"). */
export interface FacetGroup {
  key: string;
  label: string;
  values: FacetCount[];
}

export interface DataSearchResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  facets: FacetGroup[];
}

/** Filter set for the directory. Serialised into a share so the recipient replays it exactly. */
export interface DataSearchRequest {
  audience: ShareAudience;
  q?: string;
  city?: string;
  role?: string;
  availabilityStatus?: string;
  roleLevel?: string;
  domain?: string;
  expertise?: string;
  /** Generic analytics drill-down: attribute + exact value of a clicked distribution bar. */
  field?: string;
  value?: string;
  minExperience?: number | null;
  maxExperience?: number | null;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}

export interface DataShare {
  id: string;
  token: string;
  url: string;
  audience: ShareAudience;
  title: string | null;
  note: string | null;
  status: DataShareStatus;
  recipientEmails: string[];
  viewCount: number;
  createdBy: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  /** Soft-deleted shares are hidden from normal listings but can be restored. */
  deleted: boolean;
  deletedAt: string | null;
  /** eventType -> count. */
  eventCounts: Record<string, number>;
  totalEvents: number;
}

export interface DataShareEvent {
  id: string;
  eventType: ShareEventType;
  actorType: 'ADMIN' | 'RECIPIENT' | null;
  actorId: string | null;
  filtersJson: string | null;
  metadata: string | null;
  ipAddress: string | null;
  createdAt: string | null;
}

export interface CreateShareRequest {
  audience: ShareAudience;
  title?: string;
  note?: string;
  filters: DataSearchRequest;
  recipientEmails: string[];
  expiresInHours?: number | null;
}

/** Edits an existing share link; omitted fields are left unchanged by the server. */
export interface UpdateShareRequest {
  title?: string;
  note?: string;
  recipientEmails?: string[];
  expiresInHours?: number | null;
}

export interface OnboardRequest {
  audience?: ShareAudience;
  ids?: string[];
  message?: string | null;
  token?: string;
}

/** Aggregate "talent density" snapshot scoped to the active filters. */
export interface TalentStats {
  audience: ShareAudience;
  total: number;
  seniorCandidates: number;
  belowSeniorityBar: number;
  verified: number;
  profileComplete: number;
  activeLast30Days: number;
  activelyExploring: number;
  openToOffers: number;
  averageExperience: number | null;
  averageScore: number | null;
  byExperience: FacetCount[];
  byRoleLevel: FacetCount[];
  byAvailability: FacetCount[];
  byCity: FacetCount[];
  byDomain: FacetCount[];
  byCoreExpertise: FacetCount[];
  byCurrentRole: FacetCount[];
  byWorkFlexibility: FacetCount[];
  byDecisionSpeed: FacetCount[];
  generatedAt: string | null;
}

/** One "Talent Insights" card: a single attribute grouped by value with counts. */
export interface AnalyticsSection {
  key: string;
  label: string;
  category: string;
  /** When set, the card is clickable and clicking a bar drills the directory by this attribute. */
  filterField: string | null;
  total: number;
  values: FacetCount[];
}

/** Full per-field analytics catalogue for the admin "Talent Insights" view. */
export interface TalentAnalytics {
  audience: ShareAudience;
  total: number;
  generatedAt: string | null;
  sections: AnalyticsSection[];
  message: string | null;
}

export interface PublicShareView<T> {
  audience: ShareAudience;
  title: string | null;
  note: string | null;
  status: DataShareStatus;
  expiresAt: string | null;
  stats: TalentStats;
  results: DataSearchResponse<T>;
}

export const SHARE_EVENT_LABELS: Record<ShareEventType, string> = {
  LINK_CREATED: 'Link created',
  LINK_SENT: 'Link sent',
  LINK_OPENED: 'Opened',
  FILTERS_APPLIED: 'Filters applied',
  RESULT_VIEWED: 'Results viewed',
  EXPORTED: 'Exported',
  ONBOARD_CLICKED: 'Onboard clicked',
  ONBOARD_SENT: 'Onboard sent',
};

export const AVAILABILITY_OPTIONS = [
  'Actively exploring',
  'Open to selective offers',
  'Not actively looking',
];
